import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Spinner, Empty } from "@/components/common";
import { Files, X, ShieldCheck, Pencil, AlertTriangle, CheckCircle2, Eye } from "lucide-react";
import PlantillaPreview from "@/components/PlantillaPreview";

const EDITABLES = new Set(["titulo", "subtitulo", "parrafo", "aceptacion_si_no"]);

function EstadoBadge({ estado }) {
  return estado === "aprobado"
    ? <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Aprobada</span>
    : <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> Borrador</span>;
}

function EditarModal({ p, onClose, onSaved }) {
  const [nombre, setNombre] = useState(p.nombre);
  const [cuerpo, setCuerpo] = useState(p.cuerpo.map((b) => ({ ...b })));
  const [saving, setSaving] = useState(false);
  const setTexto = (i, v) => setCuerpo((prev) => prev.map((b, idx) => idx === i ? { ...b, texto: v } : b));
  const guardar = async () => {
    setSaving(true);
    try { const { data } = await api.put(`/plantillas/${p.key}`, { nombre, cuerpo }); toast.success("Plantilla actualizada (queda en borrador hasta aprobarla)"); onSaved(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 flex max-h-[88vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 p-5"><h3 className="font-display text-lg font-semibold">Editar plantilla</h3><button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button></div>
        <div className="flex-1 space-y-3 overflow-y-auto p-5">
          <div><label className="mb-1 block text-xs font-medium text-slate-500">Nombre del documento</label><input className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-vino-500" value={nombre} onChange={(e) => setNombre(e.target.value)} data-testid="plantilla-nombre" /></div>
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">Al guardar, la plantilla pasa a borrador y solo emitirá documentos de prueba hasta que vuelvas a aprobarla.</p>
          {cuerpo.map((b, i) => EDITABLES.has(b.t) ? (
            <div key={i}>
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-400">{b.t}</label>
              <textarea rows={b.t === "parrafo" ? 3 : 1} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-vino-500" value={b.texto || ""} onChange={(e) => setTexto(i, e.target.value)} />
            </div>
          ) : (
            <div key={i} className="rounded-lg border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-400">Bloque «{b.t}» (estructura fija, no editable aquí)</div>
          ))}
        </div>
        <div className="border-t border-slate-200 p-4 text-right"><button onClick={guardar} disabled={saving} data-testid="plantilla-guardar" className="rounded-lg bg-vino-700 px-5 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-60">{saving ? "Guardando…" : "Guardar cambios"}</button></div>
      </div>
    </div>
  );
}

export default function Plantillas() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const load = () => { setRows(null); api.get("/plantillas").then((r) => setRows(r.data)).catch(() => setRows([])); };
  useEffect(load, []);

  const cambiarEstado = async (key, estado) => {
    try { await api.patch(`/plantillas/${key}/estado`, null, { params: { estado } }); toast.success(estado === "aprobado" ? "Plantilla aprobada" : "Plantilla marcada como borrador"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Plantillas de documentos</h1>
        <p className="mt-1 text-sm text-slate-500">Formatos oficiales para la contratación. Solo las plantillas aprobadas emiten documentos finales; las de borrador generan documentos de prueba.</p>
      </div>

      {rows === null ? <div className="flex h-48 items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div>
        : rows.length === 0 ? <Empty icon={Files} title="Sin plantillas" subtitle="No hay plantillas configuradas." />
        : (
          <div className="space-y-3" data-testid="lista-plantillas">
            {rows.map((p) => (
              <div key={p.key} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`plantilla-${p.key}`}>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-vino-50 text-vino-700"><Files className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800">{p.nombre}</p>
                  <p className="text-xs text-slate-400">{p.codigo} · versión {p.version} · {p.requiere_firma ? "requiere firma" : "sin firma"}</p>
                </div>
                <EstadoBadge estado={p.estado} />
                <button onClick={() => setPreview(p)} data-testid={`preview-${p.key}`} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-vino-700 hover:bg-vino-50"><Eye className="h-3.5 w-3.5" /> Vista previa</button>
                <button onClick={() => setEditing(p)} data-testid={`editar-${p.key}`} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"><Pencil className="h-3.5 w-3.5" /> Editar</button>
                {p.estado === "aprobado"
                  ? <button onClick={() => cambiarEstado(p.key, "borrador")} data-testid={`borrador-${p.key}`} className="flex items-center gap-1.5 rounded-lg border border-amber-200 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50"><AlertTriangle className="h-3.5 w-3.5" /> Marcar borrador</button>
                  : <button onClick={() => cambiarEstado(p.key, "aprobado")} data-testid={`aprobar-${p.key}`} className="flex items-center gap-1.5 rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50"><ShieldCheck className="h-3.5 w-3.5" /> Aprobar</button>}
              </div>
            ))}
          </div>
        )}

      {editing && <EditarModal p={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {preview && <PlantillaPreview p={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
